import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { deviceFingerprint, shopSession, signupAPI } from "../services/api";
import { useI18n } from "../i18n";
import AuthShell, { buttonClass, inputClass } from "../components/AuthShell";
import TurnstileWidget from "../components/TurnstileWidget";

const CURRENCIES = ["TND", "EUR", "USD", "DZD", "MAD", "LYD"];

const MENU_BASE = (
  process.env.REACT_APP_MENU_URL || window.location.origin
).replace(/\/$/, "");

/**
 * The invitation code from another shop's link (?ref=). Kept for the visit, so
 * opening the sign-in page and coming back does not lose it.
 */
const REF_KEY = "signup_ref";
const readRef = () => {
  const fromLink = new URLSearchParams(window.location.search).get("ref");
  try {
    if (fromLink) sessionStorage.setItem(REF_KEY, fromLink);
    return fromLink || sessionStorage.getItem(REF_KEY);
  } catch {
    return fromLink;
  }
};

const deviceLabelKey = () => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/iPhone|Android|Mobile/i.test(ua)) return "phone";
  return "laptop";
};

/**
 * A stranger creates their own shop. They land in a working trial straight
 * away; the operator reviews the signup afterwards. This device becomes the
 * shop's first, so no activation step stands between them and the dashboard.
 */
const Signup = () => {
  const { t, lang } = useI18n();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    owner_name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
    shop_name: "",
    currency: "TND",
    slug: "",
    accepted_terms: false,
  });
  const [showLink, setShowLink] = useState(false);
  const [turnstile, setTurnstile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState({});
  const [referral, setReferral] = useState(() => ({ code: readRef(), from: null }));

  useEffect(() => {
    signupAPI
      .config()
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ enabled: false }));
  }, []);

  // Who sent the link, so the new owner knows what it means; an unknown code is dropped
  useEffect(() => {
    if (!referral.code) return;
    signupAPI
      .referral(referral.code)
      .then(({ data }) => setReferral((current) => ({ ...current, from: data.shop_name })))
      .catch(() => setReferral({ code: null, from: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, for the code it arrived with
  }, []);

  const onToken = useCallback((token) => setTurnstile(token), []);

  const set = (key) => (event) =>
    setForm((current) => ({
      ...current,
      [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
    }));

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setFieldError({});

    if (!form.accepted_terms) {
      setError(t("signup.mustAccept"));
      return;
    }

    if (form.password !== form.confirm_password) {
      setFieldError({ confirm_password: t("signup.passwordMismatch") });
      return;
    }

    setBusy(true);
    try {
      const { data } = await signupAPI.signup({
        ...form,
        confirm_password: undefined,
        slug: form.slug.trim().toLowerCase() || undefined,
        language: lang,
        turnstile_token: turnstile || undefined,
        ref: referral.code || undefined,
        device: {
          label: t(`auth.activate.labels.${deviceLabelKey()}`),
          platform: navigator.userAgent,
          fingerprint: deviceFingerprint(),
        },
      });

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      if (data.device_token) {
        shopSession.setLabel(t(`auth.activate.labels.${deviceLabelKey()}`));
        shopSession.use(data.shop.id, data.device_token);
      }
      try {
        sessionStorage.removeItem(REF_KEY);
      } catch {
        /* nothing kept */
      }

      // A full load, so the app starts again knowing it is signed in and activated
      window.location.assign("/admin");
    } catch (err) {
      const status = err.response?.status;
      const code = err.response?.data?.code;

      if (code === "EMAIL_TAKEN") setFieldError({ email: t("signup.emailTaken") });
      else if (code === "PHONE_TAKEN") setFieldError({ phone: t("signup.phoneTaken") });
      else if (code === "SLUG_TAKEN") {
        setShowLink(true);
        setFieldError({ slug: t("signup.slugTaken") });
      } else if (code === "SIGNUP_CLOSED") setConfig({ enabled: false });
      else if (status === 429) setError(t("signup.tooMany"));
      else if (status === 400 && /bot check/i.test(err.response.data?.message || "")) {
        setError(t("signup.botCheck"));
      } else {
        setError(err.response?.data?.message || t("signup.failed"));
      }
      setBusy(false);
    }
  };

  const input = (key, label, props = {}, hint) => (
    <div>
      <label htmlFor={`signup-${key}`} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        id={`signup-${key}`}
        value={form[key]}
        onChange={set(key)}
        className={`${inputClass} ${fieldError[key] ? "border-red-400" : ""}`}
        required={key !== "slug"}
        {...props}
      />
      {fieldError[key] ? (
        <p className="mt-1 text-xs text-red-600">{fieldError[key]}</p>
      ) : (
        hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>
      )}
    </div>
  );

  return (
    <AuthShell title={t("signup.title")}>
      {!config ? null : !config.enabled ? (
        <div className="space-y-5">
          <p className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
            {t("signup.closed")}
          </p>
          <Link to="/login" className="block text-center text-sm text-primary-600 hover:underline">
            {t("signup.haveAccount")}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-gray-600 text-center -mt-2">{t("signup.subtitle")}</p>

          {referral.from && (
            <p className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-900 text-sm">
              🎟️ {t("signup.invitedBy", { name: referral.from })}
            </p>
          )}

          {error && (
            <p className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</p>
          )}

          {input("owner_name", t("signup.ownerName"), { autoComplete: "name" })}
          {input("email", t("signup.email"), { type: "email", autoComplete: "email", dir: "ltr" })}
          {input("phone", t("signup.phone"), { type: "tel", autoComplete: "tel", dir: "ltr" }, t("signup.phoneHint"))}
          {input(
            "password",
            t("signup.password"),
            { type: "password", autoComplete: "new-password", minLength: 8 },
            t("signup.passwordHint"),
          )}
          {input("confirm_password", t("signup.confirmPassword"), {
            type: "password",
            autoComplete: "new-password",
            minLength: 8,
          })}
          {input("shop_name", t("signup.shopName"), { maxLength: 120 })}

          <div>
            <label htmlFor="signup-currency" className="block text-sm font-medium text-gray-700 mb-1">{t("signup.currency")}</label>
            <select id="signup-currency" value={form.currency} onChange={set("currency")} className={inputClass}>
              {CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>

          {showLink ? (
            <div>
              {input(
                "slug",
                t("signup.linkLabel"),
                { dir: "ltr", placeholder: "my-shop", autoCapitalize: "none", maxLength: 64 },
                t("signup.linkHint"),
              )}
              <p className="mt-1 text-xs text-gray-400" dir="ltr">
                {MENU_BASE}/s/{form.slug || "…"}
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowLink(true)}
              className="text-sm text-primary-600 hover:underline"
            >
              {t("signup.chooseLink")}
            </button>
          )}

          <label className="flex items-start gap-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.accepted_terms}
              onChange={set("accepted_terms")}
              className="mt-1 h-5 w-5 shrink-0"
            />
            <span>
              {t("signup.terms", {
                terms: "\u0000terms\u0000",
                privacy: "\u0000privacy\u0000",
              })
                .split("\u0000")
                .map((part, index) =>
                  part === "terms" ? (
                    <Link key={index} to="/terms" target="_blank" className="text-primary-600 underline">
                      {t("signup.termsLink")}
                    </Link>
                  ) : part === "privacy" ? (
                    <Link key={index} to="/privacy" target="_blank" className="text-primary-600 underline">
                      {t("signup.privacyLink")}
                    </Link>
                  ) : (
                    <React.Fragment key={index}>{part}</React.Fragment>
                  ),
                )}
            </span>
          </label>

          <TurnstileWidget siteKey={config.turnstile_site_key} onToken={onToken} />

          <button
            type="submit"
            disabled={busy || (Boolean(config.turnstile_site_key) && !turnstile)}
            className={buttonClass}
          >
            {busy ? t("signup.submitting") : t("signup.submit")}
          </button>

          <p className="text-center text-sm">
            <Link to="/login" className="text-primary-600 hover:underline">
              {t("signup.haveAccount")}
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
};

export default Signup;
